import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/configs/defaults'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('tokenValidation')

/**
 * Checks if two tokens are the same (same address on same chain)
 */
export function isSameToken(token1?: IToken, chainId1?: number, token2?: IToken, chainId2?: number): boolean {
    if (!token1 || !token2) return false

    const effectiveChainId1 = chainId1 ?? token1.chainId
    const effectiveChainId2 = chainId2 ?? token2.chainId

    return token1.address.toLowerCase() === token2.address.toLowerCase() && effectiveChainId1 === effectiveChainId2
}

/**
 * Checks if two exchange data objects contain the same token
 */
export function isSameExchangeToken(sellData: IExchangeData, buyData: IExchangeData): boolean {
    return isSameToken(sellData.token, sellData.chainId, buyData.token, buyData.chainId)
}

/**
 * Validates URL parameters to prevent same token on both sides
 * @returns true if parameters were valid, false if they were reset
 */
export function validateUrlTokenParams(
    params: URLSearchParams,
    updateBothData: (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => void
): boolean {
    const sellChain = params.get('sellChain')
    const buyChain = params.get('buyChain')
    const sellToken = params.get('sellToken')
    const buyToken = params.get('buyToken')

    // Check for same token on same chain
    if (sellChain && buyChain && sellToken && buyToken) {
        if (sellChain === buyChain && sellToken.toLowerCase() === buyToken.toLowerCase()) {
            logger.info('URL parameters contain same token on same chain - resetting to defaults')

            // Reset to default tokens
            updateBothData({
                sell: {
                    token: DEFAULT_SELL_TOKEN,
                    chainId: DEFAULT_SELL_TOKEN.chainId,
                    value: BigInt(0),
                },
                buy: {
                    token: DEFAULT_BUY_TOKEN,
                    chainId: DEFAULT_BUY_TOKEN.chainId,
                    value: BigInt(0),
                },
            })

            return false
        }
    }

    return true
}

/**
 * Validates and handles token selection, preventing the same token from being selected on both sides
 */
export function handleTokenSelection(
    newData: { token?: IToken; chainId?: number },
    type: 'buy' | 'sell',
    sellData: IExchangeData,
    buyData: IExchangeData,
    updateSellData: (data: Partial<IExchangeData>) => void,
    updateBuyData: (data: Partial<IExchangeData>) => void
): void {
    logger.info(`[handleTokenSelection] type=${type}, newData=`, newData)

    if (!newData.token) {
        logger.warn(`[handleTokenSelection]: invalid ${type} token selected:`, newData)
        return
    }

    if (type === 'sell') {
        // Check if selecting this would create a duplicate
        if (isSameToken(newData.token, newData.chainId, buyData.token, buyData.chainId)) {
            // Update sell side with selected token
            updateSellData({ token: newData.token, chainId: newData.token.chainId })

            // Reset buy side to default
            updateBuyData({
                token: DEFAULT_BUY_TOKEN,
                chainId: DEFAULT_BUY_TOKEN.chainId,
            })
        } else {
            // Normal update
            updateSellData({ token: newData.token, chainId: newData.token.chainId })
        }
    } else {
        // Check if selecting this would create a duplicate
        if (isSameToken(newData.token, newData.chainId, sellData.token, sellData.chainId)) {
            // Update buy side with selected token
            updateBuyData({ token: newData.token, chainId: newData.token.chainId })

            // Reset sell side to default
            updateSellData({
                token: DEFAULT_SELL_TOKEN,
                chainId: DEFAULT_SELL_TOKEN.chainId,
            })
        } else {
            // Normal update
            updateBuyData({ token: newData.token, chainId: newData.token.chainId })
        }
    }
}
